import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, Crown, Heart, MessageCircle, RefreshCw, Check, CheckCheck, Star, ChevronRight, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function NotificationsPage() {
  const { user, api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      
      // Mark all as read when viewing the page
      await api.put('/notifications/read');
    } catch (error) {
      if (error.response?.status === 403) {
        // User not subscribed - handled in UI
      } else {
        toast.error('Erreur lors du chargement des notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // If user is not subscribed, show upgrade message
  if (!user?.is_subscribed) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="notifications-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              Abonnez-vous
            </h1>
            <p className="text-gray-400 mb-2">
              Recevez des notifications quand d'autres membres interagissent avec votre profil.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Link to="/subscription" className="btn-gold inline-block" data-testid="upgrade-btn">
              Découvrir les offres
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl gold-gradient-text flex items-center gap-3">
              <Bell className="w-8 h-8" />
              Notifications
            </h1>
            <p className="text-gray-500 mt-1">
              {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={fetchNotifications} 
            className="btn-outline-gold flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Bell className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">Aucune notification pour le moment</p>
            <p className="text-gray-500 text-sm">
              Vous recevrez des notifications quand d'autres membres aimeront ou commenteront vos photos.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-list">
            {notifications.map((notification) => (
              <NotificationCard key={notification.id} notification={notification} formatTime={formatTime} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const NotificationCard = ({ notification, formatTime }) => {
  const navigate = useNavigate();
  
  const getIcon = (type) => {
    switch (type) {
      case 'photo_like':
        return <Heart className="w-5 h-5 text-red-400 fill-red-400" />;
      case 'photo_comment':
        return <MessageCircle className="w-5 h-5 text-blue-400" />;
      case 'new_testimonial':
        return <Star className="w-5 h-5 text-gold fill-gold" />;
      case 'album_shared':
        return <FolderOpen className="w-5 h-5 text-purple-400" />;
      default:
        return <Bell className="w-5 h-5 text-gold" />;
    }
  };

  const getMessage = (type) => {
    switch (type) {
      case 'photo_like':
        return 'a aimé votre photo';
      case 'photo_comment':
        return 'a commenté votre photo';
      case 'new_testimonial':
        return 'vous a laissé un témoignage';
      case 'album_shared':
        return null; // Use the message from notification
      default:
        return 'a interagi avec votre profil';
    }
  };

  // Get the destination URL based on notification type
  const getDestinationUrl = () => {
    switch (notification.type) {
      case 'photo_like':
      case 'photo_comment':
        // Navigate to profile with photo_id in state to open modal
        return `/profile?tab=public&photo=${notification.photo_id}`;
      case 'new_testimonial':
        // Navigate to profile testimonials tab
        return '/profile?tab=testimonials';
      case 'album_shared':
        // Navigate to sender's profile to see shared albums
        return `/member/${notification.sender_id}`;
      default:
        return `/member/${notification.sender_id}`;
    }
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on the avatar/name links
    if (e.target.closest('a')) return;
    
    const destination = getDestinationUrl();
    navigate(destination);
  };

  return (
    <div 
      className={`glass-card p-4 transition-all hover:border-gold/30 cursor-pointer group ${!notification.is_read ? 'border-l-4 border-l-gold bg-gold/5' : ''}`}
      data-testid="notification-card"
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <Link to={`/member/${notification.sender_id}`} onClick={(e) => e.stopPropagation()}>
          {notification.sender_profile_picture ? (
            <img 
              src={getImageUrl(notification.sender_profile_picture)}
              alt={notification.sender_pseudo}
              className="w-12 h-12 rounded-full object-cover border-2 border-gold/30 hover:border-gold transition-colors"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-semibold text-lg hover:bg-gold/30 transition-colors">
              {notification.sender_pseudo?.[0]?.toUpperCase() || '?'}
            </div>
          )}
        </Link>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {getIcon(notification.type)}
            <Link 
              to={`/member/${notification.sender_id}`}
              className="font-semibold text-gold hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {notification.sender_pseudo}
            </Link>
            {getMessage(notification.type) ? (
              <span className="text-gray-400">{getMessage(notification.type)}</span>
            ) : notification.message ? (
              <span className="text-gray-400 text-sm">{notification.message.replace(notification.sender_pseudo + ' ', '')}</span>
            ) : null}
          </div>
          
          {/* Comment preview */}
          {notification.comment_text && (
            <p className="text-gray-500 text-sm italic mt-1 truncate">
              "{notification.comment_text}"
            </p>
          )}
          
          <span className="text-xs text-gray-600 mt-1 block">
            {formatTime(notification.created_at)}
          </span>
        </div>

        {/* Photo thumbnail */}
        {notification.photo_url && (
          <div className="flex-shrink-0">
            <img 
              src={getImageUrl(notification.photo_url)}
              alt="Photo"
              className="w-16 h-16 object-cover rounded-lg border border-white/10 group-hover:border-gold/50 transition-colors"
            />
          </div>
        )}

        {/* Arrow indicator and read status */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {notification.is_read ? (
            <CheckCheck className="w-4 h-4 text-gray-600" />
          ) : (
            <div className="w-2 h-2 bg-gold rounded-full" />
          )}
          <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gold transition-colors" />
        </div>
      </div>
    </div>
  );
};
